import useController from "@pages/profile-v2/pages/profile/containers/MaximumDailyDeposit/useController";
import { Form, Input } from "antd";
import moment from "moment";
import PropTypes from "prop-types";
import React, { memo } from "react";
import styled from "styled-components";
import { BTTO_PROFILE_COLORS } from "../../shared/elements";
import { BettoPrimaryButton } from "../BettoButton";

const Root = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	width: 100%;
`;

const Description = styled.p`
	margin: 0;
	color: ${BTTO_PROFILE_COLORS.textMuted};
	font-size: 13px;
	line-height: 1.5;
`;

const Notice = styled.div`
	padding: 12px 14px;
	border-radius: 8px;
	background: ${BTTO_PROFILE_COLORS.surfaceRaised};
	border: 1px solid ${BTTO_PROFILE_COLORS.border};
	color: ${BTTO_PROFILE_COLORS.text};
	font-size: 13px;
	line-height: 1.5;
`;

const FormStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 100%;

	.ant-form-item {
		margin-bottom: 0;
		width: 100%;
	}

	.ant-form-item-label {
		padding: 0 0 8px;
		line-height: 1.4;

		> label {
			height: auto;
			color: ${BTTO_PROFILE_COLORS.textMuted};
			font-size: 12px;
			font-weight: 500;

			&::before {
				color: ${BTTO_PROFILE_COLORS.danger} !important;
			}
		}
	}

	.ant-form-item-control-input {
		min-height: auto;
	}

	.ant-input {
		width: 100%;
		height: 44px !important;
		padding: 0 14px !important;
		border-radius: 8px !important;
		border: 1px solid ${BTTO_PROFILE_COLORS.border} !important;
		background: ${BTTO_PROFILE_COLORS.surfaceRaised} !important;
		color: ${BTTO_PROFILE_COLORS.text} !important;
		font-size: 14px !important;

		&:focus,
		&:hover {
			border-color: ${BTTO_PROFILE_COLORS.borderActive} !important;
			box-shadow: none !important;
		}

		&::placeholder {
			color: ${BTTO_PROFILE_COLORS.textDim};
		}
	}
`;

export const BettoMaximumDailyDeposit = memo(({ translate, showAlert }) => {
	const {
		state: {
			isDepositLimitSet,
			depositLimitAmount,
			depositLimitStartDate,
			isSetting,
			isDeleting,
			isButtonDisabled,
		},
		handlers: { handleDepositLimit, handleFormValuesChange, handleDeleteDepositLimit },
		helpers: { AMOUNT_REGEX, form },
	} = useController({ showAlert });

	return (
		<Root>
			<Description>
				{isDepositLimitSet
					? translate("Maximum Daily Deposit Limit Desc Active")
					: translate("Maximum Daily Deposit Limit Desc")}
			</Description>

			{isDepositLimitSet ? (
				<>
					<Notice>
						{translate("depositLimit.notice", {
							date: moment(depositLimitStartDate).format("DD.MM.YYYY, HH:mm"),
							amount: depositLimitAmount,
						})}
					</Notice>
					<BettoPrimaryButton
						variant="secondary"
						fullWidth
						onClick={handleDeleteDepositLimit}
						disabled={isDeleting}>
						{translate("Remove Daily Deposit Limit")}
					</BettoPrimaryButton>
				</>
			) : (
				<Form
					form={form}
					layout="vertical"
					onFinish={handleDepositLimit}
					onValuesChange={handleFormValuesChange}>
					<FormStack>
						<Form.Item
							label={translate("Daily Deposit Limit Amount")}
							name="depositLimitAmount"
							rules={[
								{
									required: true,
									message: translate("Please input amount!"),
									pattern: AMOUNT_REGEX,
								},
							]}>
							<Input
								type="number"
								min={1}
								step="0.01"
								placeholder={translate("Enter daily deposit limit")}
								disabled={isSetting}
							/>
						</Form.Item>
						<BettoPrimaryButton
							type="submit"
							fullWidth
							disabled={isButtonDisabled || isSetting}>
							{translate("Set Daily Deposit Limit")}
						</BettoPrimaryButton>
					</FormStack>
				</Form>
			)}
		</Root>
	);
});

BettoMaximumDailyDeposit.displayName = "BettoMaximumDailyDeposit";

BettoMaximumDailyDeposit.propTypes = {
	translate: PropTypes.func.isRequired,
	showAlert: PropTypes.func,
};
